import { useNavigate } from 'react-router-dom';
import {
  Input,
  TextArea,
  FormSelect,
  NumericField,
  Button,
  ModalLayout,
  StatusMessage,
  useToast,
  Checkbox,
} from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useIntegrations } from '../../context';
import { useIntegrationsPermissions } from '../../hooks';
import { useRequestIntegrationForm } from '../../hooks/request';
import ChoiceChipGroup from './parts/ChoiceChipGroup.jsx';
import FieldLabel from './parts/FieldLabel.jsx';
import ModalFooter from './parts/ModalFooter.jsx';
import PriorityCardGrid from './parts/PriorityCardGrid.jsx';
import RequestSection from './parts/RequestSection.jsx';
import DuplicateRequestNotice from './parts/DuplicateRequestNotice.jsx';
import { AlertTriangle, ArrowRight, Info, Layers } from 'lucide-react';

const DEPARTMENTS = [
  'product',
  'engineering',
  'sales',
  'marketing',
  'customer_success',
  'finance',
  'hr',
  'other',
];
const SENSITIVITY_KEYS = ['pii', 'financial', 'compliance'];
const SENSITIVITY_LEVELS = ['no', 'maybe', 'yes'];
const SCOPE_OPTIONS = ['tenant', 'org'];
const ACTION_TYPES = ['read', 'write', 'push'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];

const COMPACT_INPUT_CLASS = 'h-9 py-2 text-[12.5px] leading-snug rounded-lg';
// Match the input height exactly: force 36px and override FormSelect's default
// `min-h-[2.75rem]` (44px) floor, which otherwise makes the select taller.
const FORM_SELECT_TRIGGER_CLASS = 'h-9 !min-h-0 py-2 text-[12.5px] leading-snug rounded-lg';

/**
 * Request Integration modal (CT-4175). Layout follows the prototype `.req-modal`
 * (numbered sections, chip rows, 2×2 priority cards) using `@aimsos/ui` primitives.
 * Validation/submit is CT-4176.
 *
 * Confirmation (CT-4177): on success the modal closes and a top-right success
 * toast reports the ticket number (matches the prototype `submitRequestModal`,
 * which closes + toasts rather than swapping an in-modal screen). The end-user
 * branch also routes to My Requests, but it is dormant in V1 —
 * `canManageIntegrations` is true for everyone until RBAC lands (CT-3900), so the
 * effective V1 behavior is the admin branch (toast only, no redirect).
 */
function RequestIntegrationModal() {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const { isRequestOpen, requestPrefill, closeRequestIntegration } = useIntegrations();
  const { canManageIntegrations } = useIntegrationsPermissions();
  const toast = useToast();
  const fm = useRequestIntegrationForm({
    prefillName: requestPrefill?.prefillName,
    isOpen: isRequestOpen,
  });
  const {
    form,
    setField,
    setSensitivity,
    toggleActionType,
    errors,
    canSubmit,
    submitError,
    isSubmitting,
    duplicateConnected,
    duplicatePending,
    submitAnyway,
    setSubmitAnyway,
  } = fm;

  // On a successful submit: close the modal and confirm via a top-right toast
  // with the ticket number (CT-4177). Role-branched behind `canManageIntegrations`
  // — end users land on My Requests; admins stay put. `submit` resolves to the
  // ticket number (or null on validation/API failure, which keeps the modal open
  // with the retryable error banner).
  const handleSubmit = async () => {
    const ticket = await fm.submit();
    if (!ticket) return;
    toast.success({
      title: t('integrations.request.confirmation.title'),
      message: t(
        canManageIntegrations
          ? 'integrations.request.confirmation.toastAdmin'
          : 'integrations.request.confirmation.toastUser',
        { ticket },
      ),
      canClose: true,
    });
    closeRequestIntegration();
    // End users land on the Requests surface, which renders their "My requests"
    // read-only view when the persona is user (CT-4203). Dormant in V1 until RBAC
    // (CT-3900) makes canManageIntegrations vary for real end users.
    if (!canManageIntegrations) navigate('/admin-studio/requests');
  };

  // CT-4205 duplicate-detection links: close the modal, then route to the
  // connected provider (Case 1) or the requester's own requests (Case 2).
  const goAndClose = (path) => {
    closeRequestIntegration();
    navigate(path);
  };

  if (!isRequestOpen) return null;

  const sensitivityOptions = SENSITIVITY_LEVELS.map((value) => ({
    value,
    label: t(`integrations.request.sensitivityLevel.${value}`),
  }));

  const estimatedUsers =
    form.expected_users === '' ? null : Number(form.expected_users);

  return (
    <ModalLayout
      isOpen={isRequestOpen}
      onClose={closeRequestIntegration}
      title={t('integrations.request.title')}
      subtitle={t('integrations.request.subtitle')}
      maxWidthClassName="max-w-[600px]"
      titlePrefix={
        <div
          aria-hidden="true"
          className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] border border-sky-400/30 bg-sky-400/10 text-[#7ed3f7]"
        >
          <Layers size={16} strokeWidth={1.7} />
        </div>
      }
    >
      <div className="max-h-[min(70vh,calc(92vh-9rem))] overflow-y-auto px-6 pb-2 pt-[18px] scrollbar-aimsos-auto-sm">
        {submitError && (
          <div className="mb-[11px] flex flex-col items-center gap-2">
            <StatusMessage
              icon={AlertTriangle}
              title={t('common.requestFailed')}
              subtitle={submitError}
            />
            <Button variant="secondary" size="sm" loading={isSubmitting} onClick={handleSubmit}>
              {t('common.retry')}
            </Button>
          </div>
        )}
        {/* Duplicate detection (CT-4205): compact amber callout before a dupe. */}
        {duplicateConnected && (
          <DuplicateRequestNotice
            icon={Info}
            title={t('integrations.request.duplicate.connected')}
            actionLabel={t('integrations.request.duplicate.goTo', { name: duplicateConnected.name })}
            onAction={() =>
              duplicateConnected.slug &&
              goAndClose(`/admin-studio/integrations/${duplicateConnected.slug}`)
            }
          />
        )}
        {duplicatePending && (
          <DuplicateRequestNotice
            icon={AlertTriangle}
            title={t('integrations.request.duplicate.pending', { name: duplicatePending.name })}
            actionLabel={t('integrations.request.duplicate.view')}
            onAction={() => goAndClose('/admin-studio/requests')}
          >
            <label className="flex items-center gap-2 text-[12.5px] text-[#92400E] dark:text-[#FDE68A]">
              <Checkbox checked={submitAnyway} onChange={() => setSubmitAnyway(!submitAnyway)} />
              {t('integrations.request.duplicate.submitAnyway')}
            </label>
          </DuplicateRequestNotice>
        )}

        <RequestSection number={1} title={t('integrations.request.sections.who')}>
          <div className="grid grid-cols-1 gap-[11px] sm:grid-cols-2">
            <div className="flex flex-col gap-[5px]">
              <FieldLabel htmlFor="req-requester">
                {t('integrations.request.fields.requester')}
              </FieldLabel>
              <Input
                id="req-requester"
                value={form.requester}
                readOnly
                className={COMPACT_INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-[5px]">
              <FieldLabel htmlFor="req-department">
                {t('integrations.request.fields.department')}
              </FieldLabel>
              <FormSelect
                id="req-department"
                value={form.department}
                onChange={(value) => setField('department', value)}
                triggerClassName={FORM_SELECT_TRIGGER_CLASS}
                options={DEPARTMENTS.map((dept) => ({
                  value: dept,
                  label: t(`integrations.request.departments.${dept}`),
                }))}
              />
            </div>
          </div>
          <div className="mt-[9px]">
            <ChoiceChipGroup
              label={t('integrations.request.fields.scope')}
              value={form.scope}
              onChange={(value) => setField('scope', value)}
              options={SCOPE_OPTIONS.map((value) => ({
                value,
                label: t(`integrations.request.scope.${value}`),
              }))}
            />
          </div>
        </RequestSection>

        <RequestSection number={2} title={t('integrations.request.sections.integration')}>
          <div className="grid grid-cols-1 gap-[11px] sm:grid-cols-2">
            <div className="flex flex-col gap-[5px]">
              <FieldLabel htmlFor="req-name" required>
                {t('integrations.request.fields.name')}
              </FieldLabel>
              <Input
                id="req-name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                onBlur={() => fm.validateField('name')}
                placeholder={t('integrations.request.fields.namePlaceholder')}
                error={errors.name ? t(`integrations.request.errors.${errors.name}`) : undefined}
                className={COMPACT_INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-[5px]">
              <FieldLabel htmlFor="req-vendor">
                {t('integrations.request.fields.vendor')}
              </FieldLabel>
              <Input
                id="req-vendor"
                value={form.vendor}
                onChange={(e) => setField('vendor', e.target.value)}
                placeholder={t('integrations.request.fields.vendorPlaceholder')}
                className={COMPACT_INPUT_CLASS}
              />
            </div>
          </div>
          <div className="mt-[9px] flex flex-col gap-[5px]">
            <FieldLabel htmlFor="req-url" required>
              {t('integrations.request.fields.url')}
            </FieldLabel>
            <Input
              id="req-url"
              type="url"
              value={form.url}
              onChange={(e) => setField('url', e.target.value)}
              onBlur={() => fm.validateField('url')}
              placeholder={t('integrations.request.fields.urlPlaceholder')}
              helperText={t('integrations.request.fields.urlHint')}
              error={errors.url ? t(`integrations.request.errors.${errors.url}`) : undefined}
              className={COMPACT_INPUT_CLASS}
            />
          </div>
          <div className="mt-[9px] flex flex-col gap-[5px]">
            <FieldLabel htmlFor="req-intent" required>
              {t('integrations.request.fields.intent')}
            </FieldLabel>
            <TextArea
              id="req-intent"
              value={form.business_intent}
              onChange={(e) => setField('business_intent', e.target.value)}
              onBlur={() => fm.validateField('business_intent')}
              placeholder={t('integrations.request.fields.intentPlaceholder')}
              helperText={t('integrations.request.fields.intentHint')}
              error={
                errors.business_intent
                  ? t(`integrations.request.errors.${errors.business_intent}`)
                  : undefined
              }
              rows={3}
              className="min-h-16 text-[12.5px] leading-normal"
            />
          </div>
          <div className="mt-[9px]">
            <ChoiceChipGroup
              label={t('integrations.request.fields.actionTypes')}
              multiple
              value={form.action_types}
              onChange={toggleActionType}
              options={ACTION_TYPES.map((value) => ({
                value,
                label: t(`integrations.request.actionTypes.${value}`),
              }))}
            />
          </div>
        </RequestSection>

        <RequestSection number={3} title={t('integrations.request.sections.sensitivity')}>
          <div className="flex flex-col gap-[11px]">
            {SENSITIVITY_KEYS.map((key) => (
              <ChoiceChipGroup
                key={key}
                label={t(`integrations.request.sensitivity.${key}`)}
                value={form.data_sensitivity[key]}
                onChange={(value) => setSensitivity(key, value)}
                options={sensitivityOptions}
              />
            ))}
          </div>
        </RequestSection>

        <RequestSection number={4} title={t('integrations.request.sections.priority')}>
          <div className="flex flex-col gap-[5px]">
            <span className="text-[11px] font-semibold text-content-secondary">
              {t('integrations.request.fields.priority')}
            </span>
            <PriorityCardGrid
              value={form.priority}
              onChange={(value) => setField('priority', value)}
              options={PRIORITIES.map((value) => ({
                value,
                label: t(`integrations.request.priority.${value}`),
                description: t(`integrations.request.priorityHint.${value}`),
              }))}
            />
            <p className="mt-0.5 text-[10.5px] leading-normal text-content-muted">
              {t('integrations.request.fields.priorityScaleHint')}
            </p>
          </div>
          <div className="mt-[9px] max-w-[200px]">
            <NumericField
              id="req-users"
              size="sm"
              label={t('integrations.request.fields.expectedUsers')}
              value={Number.isFinite(estimatedUsers) ? estimatedUsers : null}
              onValueChange={(next) =>
                setField('expected_users', next == null ? '' : String(next))
              }
              min={1}
              step={1}
              showStepper
              placeholder="10"
            />
          </div>
        </RequestSection>
      </div>

      <ModalFooter className="bg-white/[0.015] px-6 py-3.5">
        <Button variant="secondary" size="md" onClick={closeRequestIntegration}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="classicPrimary"
          size="md"
          rightIcon={ArrowRight}
          disabled={!canSubmit || isSubmitting}
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {t('integrations.request.submit')}
        </Button>
      </ModalFooter>
    </ModalLayout>
  );
}

export default RequestIntegrationModal;
